import { apiService } from '../api';
import { Enrollment, EnrollmentStatus, Pagination } from '../../types/sessions';

export interface ListEnrollmentsFilter {
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string | null;   // null = section-less only
  status?: EnrollmentStatus | EnrollmentStatus[];
  search?: string;
  /** Canonical 6-digit student code. Backend does exact match on full length,
   *  prefix match on partial input — matches the fee module UX. */
  grNumber?: string;
  /** Only students with no photo on record. */
  withoutPhoto?: boolean;
  page?: number;
  limit?: number;
}

export interface CreateEnrollmentForExistingInput {
  studentId: string;
  sessionId: string;
  classId: string;
  sectionInstanceId?: string | null;
  rollNumber?: number;
}

export interface UpdateEnrollmentInput {
  rollNumber?: number;
  sectionInstanceId?: string | null;
}

export interface TransferClassInput {
  targetClassId: string;
  targetSectionInstanceId?: string | null;
  rollNumber?: number;
  reason?: string;
}

export interface ExitEnrollmentInput {
  status: 'transferred_out' | 'dropped_out';
  reason: string;
}

// Sent when the caller doesn't specify a status — backend's `listEnrollments`
// defaults a missing status to `'enrolled'`, which would hide historical rows
// in past sessions (promoted/graduated/dropped_out/etc). Sending the full enum
// matches via $in regardless of lifecycle stage.
const ALL_STATUSES: EnrollmentStatus[] = [
  'enrolled', 'transferred_internally', 'transferred_out', 'dropped_out',
  'graduated', 'promoted', 'retained', 'inconclusive'
];

/** Query params for the enrollments list — also sent to /id-cards/students. */
export const buildListParams = (filter: ListEnrollmentsFilter) => {
  const params: Record<string, unknown> = {};
  if (filter.sessionId)         params.sessionId = filter.sessionId;
  if (filter.classId)           params.classId   = filter.classId;
  if (filter.sectionInstanceId === null)                  params.sectionInstanceId = 'null';
  else if (typeof filter.sectionInstanceId === 'string')  params.sectionInstanceId = filter.sectionInstanceId;
  const effectiveStatus = filter.status ?? ALL_STATUSES;
  params.status = Array.isArray(effectiveStatus) ? effectiveStatus.join(',') : effectiveStatus;
  if (filter.search) params.search = filter.search;
  if (filter.grNumber) params.grNumber = filter.grNumber;
  if (filter.withoutPhoto) params.withoutPhoto = 'true';
  if (filter.page)   params.page   = filter.page;
  if (filter.limit)  params.limit  = filter.limit;
  return params;
};

export const enrollmentsApi = {
  list: async (filter: ListEnrollmentsFilter = {}) => {
    const response = await apiService.get<Enrollment[]>('/enrollments', buildListParams(filter));
    return {
      data: response.data ?? [],
      pagination: (response as any).pagination as Pagination | undefined
    };
  },

  get: async (id: string) => {
    const response = await apiService.get<Enrollment>(`/enrollments/${id}`);
    return response.data!;
  },

  listForStudent: async (studentId: string) => {
    const response = await apiService.get<Enrollment[]>(`/enrollments/student/${studentId}`);
    return response.data ?? [];
  },

  createForExistingStudent: async (input: CreateEnrollmentForExistingInput) => {
    const response = await apiService.post<Enrollment>('/enrollments', input);
    return response.data!;
  },

  update: async (id: string, input: UpdateEnrollmentInput) => {
    const response = await apiService.put<Enrollment>(`/enrollments/${id}`, input);
    return response.data!;
  },

  transferClass: async (id: string, input: TransferClassInput) => {
    const response = await apiService.post<Enrollment>(`/enrollments/${id}/transfer-class`, input);
    return response.data!;
  },

  exit: async (id: string, input: ExitEnrollmentInput) => {
    const response = await apiService.post<Enrollment>(`/enrollments/${id}/exit`, input);
    return response.data!;
  }
};
